import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from './AppShell';
import './Help.css';

const SUPPORT_EMAIL = 'info@svodx.ru';

const QUESTION_TYPES = [
    {
        name: 'Один вариант',
        use: 'Выбрать один ответ из списка: «Да / Нет», выбор темы, голосование.',
        results: 'Число и доля голосов за каждый вариант; график столбцами, горизонтальный или круговой.',
    },
    {
        name: 'Несколько вариантов',
        use: 'Можно отметить несколько ответов: «Какие темы вам интересны?».',
        results: 'Доля участников, выбравших каждый вариант. Сумма может быть больше 100%.',
    },
    {
        name: 'Вариант «Другое»',
        use: 'Дополнение к вопросам «Один вариант» и «Несколько вариантов»: участник выбирает «Другое» и пишет свой ответ строкой.',
        results: 'Отдельный столбец на графике, а под ним — облако своих ответов целыми фразами. Ответы проходят автопроверку.',
    },
    {
        name: 'Ранжирование',
        use: 'Расставить варианты по порядку важности или предпочтения.',
        results: 'Баллы: 1-е место приносит больше всего. Варианты отсортированы по сумме баллов.',
    },
    {
        name: 'Оценка (NPS)',
        use: 'Поставить от 1 до 5 или от 1 до 10 звёзд: удовлетворённость, готовность рекомендовать.',
        results: 'Средняя оценка и распределение по звёздам. В отчёте — NPS для шкалы 10 и доля довольных для шкалы 5.',
    },
    {
        name: 'Число',
        use: 'Ответ одним числом: возраст, стаж, оценка в процентах. Можно задать минимум и максимум.',
        results: 'Среднее, минимум, максимум и распределение значений.',
    },
    {
        name: 'Облако слов',
        use: 'Свободный короткий ответ: «Что запомнилось?», «Опишите лекцию одним словом».',
        results: 'Облако целыми фразами или по словам: чем чаще ответ, тем он крупнее. Ответы проходят модерацию.',
    },
    {
        name: 'Сетка (картинка)',
        use: 'Загрузите изображение — оно делится на ячейки (до 10 строк и столбцов), участник выбирает ячейку: точку на схеме, фото, слайд.',
        results: 'Сколько раз выбрали каждую ячейку.',
    },
    {
        name: 'Тепловая карта',
        use: 'Загрузите картинку — макет, слайд, схему, плакат. Участник нажимает на неё и ставит точку (можно перетащить, рядом с пальцем — лупа). Можно разрешить до трёх точек. Отвечать — только в браузере: Telegram-бот этот вопрос пропускает.',
        results: 'Живая тепловая карта кликов, все точки или найденные зоны. Самые популярные места сервис выделяет сам: «Зона A — вверху слева, 42%». Пока кликов меньше 10, зоной считается каждое отмеченное место. Зоны — и в отчёте на почту, координаты — в выгрузке Excel.',
    },
];

const TARIFF_ROWS = [
    ['Опросов всего', '2', '15', 'без ограничений'],
    ['Архив завершённых опросов', 'есть, в лимите 2', 'без ограничений, вне лимита', 'без ограничений'],
    ['Активных одновременно', '1', '5', '25'],
    ['Все типы вопросов, QR-коды, результаты онлайн', '✓', '✓', '✓'],
    ['Редактирование опроса после создания', '—', '✓', '✓'],
    ['Отключение перехода к результатам после ответа', '—', '✓', '✓'],
    ['Отчёт с ИИ-резюме на email', '—', '✓', '✓'],
    ['Вопросы из зала', '—', '✓', '✓'],
    ['Поделиться презентацией: до 3 файлов по 25 МБ', '—', '✓', '✓'],
    ['Встраивание результатов на сайт, в LMS и вебинар (iframe)', '—', '✓', '✓'],
    ['Выгрузка ответов в Excel и очистка ответов', '—', '—', '✓'],
    ['Логотип компании на страницах опроса', '—', '—', '✓'],
];

const SECTIONS = [
    {
        id: 'start',
        title: 'Быстрый старт',
        content: (
            <>
                <ol className="help-steps">
                    <li><b>Создайте опрос.</b> В кабинете нажмите «Создать опрос», введите название и описание. Сразу откроется редактор — добавьте вопросы.</li>
                    <li><b>Активируйте опрос.</b> Новый опрос создаётся неактивным: пока он не активирован, участники не могут отвечать.</li>
                    <li><b>Пригласите участников.</b> Покажите QR-код на экране или отправьте ссылку. Отвечать можно в Telegram или прямо на сайте.</li>
                    <li><b>Смотрите результаты.</b> Откройте страницу результатов (кнопка «Ссылки» → «Ссылка на результаты») — графики обновляются по мере ответов.</li>
                    <li><b>Завершите опрос.</b> Деактивируйте его или нажмите «Отчёт», чтобы завершить и получить сводку на почту (платные тарифы).</li>
                </ol>
                <p>Пошаговое обучение по кабинету можно пройти заново: пункт «Обучение» в меню кабинета (на телефоне — под кнопкой ☰).</p>
            </>
        ),
    },
    {
        id: 'polls',
        title: 'Опросы и их статусы',
        content: (
            <>
                <p>У опроса два статуса:</p>
                <ul>
                    <li><b>Активен</b> — участники могут отвечать по QR-коду и ссылкам.</li>
                    <li><b>Не активен</b> — ответы не принимаются, но результаты и ссылки сохраняются. Опрос можно снова активировать в любой момент.</li>
                </ul>
                <p>Фильтр «Неактивные» над списком опросов помогает быстро найти опросы, которые вы забыли запустить: если такие есть, он подсвечен.</p>
                <p><b>Активность опроса</b> видна в карточке: сколько <b>респондентов</b> (участников, отправивших опрос; каждый считается один раз), сколько <b>ответов</b> на вопросы получено (несколько вариантов в одном вопросе — один ответ; пропущенные необязательные вопросы не считаются) и когда пришёл <b>последний ответ</b>. У запущенных опросов числа обновляются сами раз в 30 секунд, пока кабинет открыт.</p>
                <p><b>Архив.</b> Завершённый опрос можно убрать из списка, не удаляя: меню «Ещё» → «В архив». Если опрос ещё принимает ответы, сервис спросит, остановить ли приём. В архиве опрос не принимает ответов (участник по ссылке увидит «Опрос завершён»), а результаты, отчёт и выгрузка в Excel остаются доступны на вкладке «Архив». Кнопка «Вернуть из архива» возвращает опрос в список неактивным — его снова можно изменить и запустить.</p>
                <p>На расширенном и корпоративном тарифах архивные опросы не ограничены и не занимают места в лимите «Опросов всего»: отправив опрос в архив, вы освобождаете место под новый. Вернуть опрос из архива можно, если в лимите есть свободное место. На бесплатном тарифе архив тоже есть, но архивные опросы считаются в лимит из 2 опросов.</p>
                <p><b>Ссылки</b> на опрос и результаты копируются кнопкой «Копировать» — она на пару секунд сменится на «✓ Скопировано».</p>
                <p>Количество опросов и сколько из них могут быть активны одновременно, зависит от тарифа — см. раздел <a href="#tariffs">Тарифы</a>.</p>
                <div className="help-callout">
                    На бесплатном тарифе редактировать опрос после создания нельзя — добавьте все вопросы сразу в открывшемся редакторе.
                </div>
                <p><b>Редактор опроса.</b> Тип нового вопроса выбирается карточкой с подсказкой, картинку можно перетащить прямо в форму, а в вариантах ответа Enter переходит к следующему или добавляет новый. В карточке вопроса: стрелки ↑ ↓ слева меняют порядок вопросов, тумблеры — «Обязательный» и «Другое», карандаш — правка текста, корзина — удаление. Тип готового вопроса можно сменить только между «Один вариант», «Несколько вариантов» и «Ранжирование»: у них общие варианты ответа. Все изменения сохраняются сразу.</p>
                <p><b>Изменить варианты ответа</b> у готового вопроса можно на тарифах «Расширенный» и «Корпоративный»: в редакторе опроса нажмите «Изменить варианты» в карточке вопроса. Варианты можно переименовать, поменять местами (стрелки ↑ ↓), добавить и удалить — минимум два. Вариант «Другое» включается отдельным тумблером и всегда стоит последним.</p>
                <p>Менять варианты можно, пока на вопрос никто не ответил: иначе голоса за «Да» могли бы превратиться в голоса за что-то другое. Если ответы уже есть, а вопрос нужно поправить, очистите ответы опроса (действие «Очистить ответы» в карточке опроса) или добавьте новый вопрос.</p>
            </>
        ),
    },
    {
        id: 'question-types',
        title: 'Типы вопросов',
        content: (
            <div className="help-types">
                {QUESTION_TYPES.map((t) => (
                    <div key={t.name} className="help-type">
                        <h3>{t.name}</h3>
                        <p><span className="help-type__label">Когда использовать:</span> {t.use}</p>
                        <p><span className="help-type__label">В результатах:</span> {t.results}</p>
                    </div>
                ))}
            </div>
        ),
    },
    {
        id: 'participants',
        title: 'Как отвечают участники',
        content: (
            <>
                <p>В карточке опроса есть два QR-кода и две ссылки для участников:</p>
                <ul>
                    <li><b>Telegram</b> — открывает бота, опрос начинается сразу. Один Telegram-аккаунт — один ответ.</li>
                    <li><b>Сайт</b> — опрос открывается в браузере, без регистрации и приложений. Вопросы идут по одному, с кнопками «Назад» и «Далее». Один браузер — один ответ.</li>
                </ul>
                <p>После ответа участник либо переходит на страницу результатов, либо видит «Спасибо за участие» — это настраивается в опросе (галочка «Перенаправлять респондентов на результаты»).</p>
                <p>Если в опросе включено <b>«Поделиться презентацией»</b>, после ответа участник увидит форму «Получить презентацию»: оставит почту — и получит ссылку на вашу презентацию. Подробнее — в разделе «Поделиться презентацией».</p>
                <p><b>Обязательные вопросы.</b> В редакторе опроса у вопроса можно включить тумблер «Обязательный». Участник увидит его со звёздочкой <b>*</b> и не сможет перейти дальше или отправить ответы, пока не ответит. Если участник начал отвечать и пытается закрыть страницу, браузер предупредит, что ответы не отправлены. Остальные вопросы можно пропускать.</p>
                <p>Если опрос не активен, участник увидит сообщение «Опрос не активен» и не сможет ответить.</p>
            </>
        ),
    },
    {
        id: 'results',
        title: 'Результаты в реальном времени',
        content: (
            <>
                <p>Ссылка на результаты — в карточке опроса, кнопка «Результаты» (у активного опроса) или «Ссылки». Откройте её на проекторе или большом экране: графики обновляются автоматически, перезагружать страницу не нужно. На странице результатов есть QR-код, по которому можно присоединиться к опросу.</p>
                <p><b>Оформление для участников.</b> При создании опроса (и потом в редакторе) выберите «Светлое» или «Тёмное». Тёмное удобно для проектора в затемнённом зале: оно действует на странице голосования, результатов и во встроенном окне. Для вопросов из зала оформление выбирается при создании сессии и в её настройках.</p>
                <p><b>Презентация спикера.</b> Если в опросе включено «Поделиться презентацией», рядом с QR-кодом для голосования на странице результатов появляется второй — «Получить презентацию». Его можно выключить в настройках опроса.</p>
                <p>Для вопросов с вариантами можно переключать вид графика: вертикальные столбцы, горизонтальные полосы или круговая диаграмма. По умолчанию вид подбирается сам: если вариантов много или подписи длинные и не помещаются под столбцами, показываются горизонтальные полосы.</p>
                <div className="help-callout">Страницу результатов видит любой, у кого есть ссылка. Не публикуйте её, если результаты должны оставаться закрытыми.</div>
            </>
        ),
    },
    {
        id: 'qa',
        title: 'Вопросы из зала',
        content: (
            <>
                <p>Участники задают вопросы спикеру и голосуют за чужие — самые интересные поднимаются наверх. Вопросы анонимны: имя не спрашивается. Это отдельный раздел, опрос для него не нужен.</p>
                <ol className="help-steps">
                    <li>В левом меню откройте <b>«Вопросы из зала»</b> и нажмите <b>«Новая сессия»</b>. Сессия — это вопросы к одному выступлению: у неё своё название, ссылка и QR-код.</li>
                    <li>Покажите участникам QR-код или ссылку. Приём вопросов можно открыть сразу или включить перед началом выступления.</li>
                    <li>Нажмите <b>«Экран для проектора»</b> — там крупно видны популярные вопросы и QR-код. Экран обновляется сам. Его же можно встроить на сайт или в вебинар: код — в блоке «Встроить экран».</li>
                    <li>Хотите поделиться слайдами? В блоке «Настройки» сессии включите <b>«Поделиться презентацией»</b> и выберите файл — у участников под формой вопроса появится «Получить презентацию спикера на почту», а на экране для проектора — QR-код «Получить презентацию».</li>
                    <li>Во время ответа нажмите <b>«На экран»</b> у вопроса — он появится крупно с подписью «Обсуждаем». После ответа отметьте <b>«Отвечен»</b>.</li>
                </ol>
                <p>Настройки модерации:</p>
                <ul>
                    <li><b>Автомодерация</b> — оскорбления и спам отклоняются автоматически (ИИ, если подключён, иначе — список запрещённых слов).</li>
                    <li><b>Премодерация</b> — вопрос виден всем только после того, как вы нажмёте «Опубликовать».</li>
                </ul>
                <p>Ненужный вопрос можно скрыть, а отклонённый или скрытый — вернуть. Один участник может задать до 10 вопросов, не чаще раза в 10 секунд.</p>
                <p>После выступления нажмите <b>«Скачать в Excel»</b> — в файле все вопросы с голосами и отметкой, отвечен ли вопрос. Прошлые сессии хранятся в списке: их можно переименовать, снова открыть приём или удалить.</p>
                <p>Доступно на расширенном и корпоративном тарифах.</p>
            </>
        ),
    },
    {
        id: 'presentations',
        title: 'Поделиться презентацией',
        content: (
            <>
                <p>Участники оставляют почту после ответа на опрос или рядом с формой вопроса из зала — и получают письмо со ссылкой на вашу презентацию. А вы видите, сколько человек её запросили и скачали.</p>
                <ol className="help-steps">
                    <li><b>Загрузите файл.</b> В левом меню откройте <b>«Ваши презентации»</b>, отметьте «Я подтверждаю…» (вы отвечаете за содержимое файла) и перетащите файл или нажмите «Выбрать файл». Подходят PDF, PPTX и ODP до 25 МБ. Одновременно хранится до 3 файлов — чтобы загрузить новый, удалите старый. Через 30 дней файл удаляется сам; за 3 дня до этого карточка подсвечивается.</li>
                    <li><b>Дождитесь проверки.</b> Файл проверяется автоматически — обычно до минуты, всё это время он в статусе «Проверяется». Если что-то покажется подозрительным, файл посмотрит модератор («На проверке у модератора»); письма уйдут после проверки.</li>
                    <li><b>Подключите к опросу или сессии.</b> В настройках опроса (<b>«Редактировать»</b>) или сессии «Вопросы из зала» включите <b>«Поделиться презентацией»</b> и выберите файл. Можно выбрать <b>«Загружу позже»</b> — почты сохранятся, и письма уйдут сами, как только вы выберете файл. Один файл можно подключить к нескольким опросам и сессиям.</li>
                    <li><b>Покажите QR-код.</b> На экране результатов и на экране для проектора появляется QR-код <b>«Получить презентацию»</b>. Его можно выключить в тех же настройках.</li>
                </ol>
                <p><b>Что видит участник.</b> После ответа — форму с почтой на экране «Голос принят» или вверху страницы результатов (только тот, кто сейчас ответил, — на проекторе её нет; есть кнопка «Не нужно»). В «Вопросах из зала» — свёрнутый блок под формой вопроса. Участник обязательно соглашается на обработку почты, а по желанию — на новости СВОД и на передачу почты вам.</p>
                <p><b>Письмо.</b> Кнопка «Скачать презентацию» открывает страницу презентации, и скачивание начинается само. На странице и в письме указано, что файл загрузил организатор и отвечает за его содержимое, и есть ссылка «Пожаловаться». В письме — личный промокод: скидка 50% на первую оплату тарифа «Расширенный» (действует 30 дней).</p>
                <p><b>Что видно в «Ваших презентациях»:</b></p>
                <ul>
                    <li>к каким опросам и сессиям подключён каждый файл — по ссылке открываются их настройки;</li>
                    <li>сколько человек запросили, получили и скачали презентацию, сколько зарегистрировались в СВОД и оплатили по промокоду;</li>
                    <li>список запросов: полный адрес — только у тех, кто отметил «Передать мой email организатору» (они же попадают в «Скачать почты»), остальные скрыты (an***@mail.ru). Запросы хранятся 90 дней;</li>
                    <li>заблокированные модератором файлы — с причиной. Заблокированный файл удаляется и не занимает место в лимите; вам приходит письмо.</li>
                </ul>
                <p><b>Жалобы.</b> Получатель может пожаловаться на файл — он сразу скрывается, пока модератор его не проверит. Удалённый вами файл тоже перестаёт открываться по ссылкам из уже отправленных писем.</p>
                <p><b>Какие файлы не принимаются:</b> с макросами (.pptm), скриптами, встроенными объектами и программами. Совет: сохраните презентацию в PDF без вложений — она легче, откроется на любом телефоне и пройдёт проверку быстрее.</p>
                <p>Доступно на расширенном и корпоративном тарифах.</p>
            </>
        ),
    },
    {
        id: 'embed',
        title: 'Встраивание на сайт, в Moodle и вебинар',
        content: (
            <>
                <p>Результаты опроса можно показать прямо на своей странице — на сайте, в курсе LMS или в вебинарной комнате. Графики обновляются в реальном времени, как на странице результатов.</p>
                <ol className="help-steps">
                    <li>В карточке опроса нажмите <b>«Встроить»</b> (на узком экране — в меню «Ещё»).</li>
                    <li>Выберите, что показать: один вопрос или все (по одному, с кнопками переключения ‹ ›), результаты или экран вопросов из зала. Содержимое подстраивается под окно — прокручивать ничего не нужно. Можно добавить QR-код для голосования, выбрать тип диаграммы (или оставить «Автоматически») и высоту окна. Зрители тоже могут переключать тип диаграммы кнопками у вопроса.</li>
                    <li>Нажмите <b>«Копировать код»</b> и вставьте его в HTML-блок своей страницы. «Предпросмотр» покажет, как это будет выглядеть.</li>
                </ol>
                <ul>
                    <li><b>Tilda</b> — блок «Другое → T123 HTML-код»; <b>WordPress</b> — блок «Произвольный HTML».</li>
                    <li><b>Moodle</b> — элемент «Страница» или «Пояснение», в редакторе кнопка «&lt;/&gt;» (HTML-код). Если код пропадает после сохранения, попросите администратора разрешить iframe или добавьте ресурс «Гиперссылка» с отображением «Встроить».</li>
                    <li><b>Вебинарная комната</b> — если платформа умеет показывать веб-страницу, вставьте код или ссылку; если нет — покажите предпросмотр через демонстрацию экрана.</li>
                </ul>
                <div className="help-callout">Встроенные результаты видит любой посетитель страницы, куда вставлен код. Если организатор перейдёт на бесплатный тариф, вместо результатов появится сообщение о недоступности.</div>
                <p>Доступно на расширенном и корпоративном тарифах.</p>
            </>
        ),
    },
    {
        id: 'wordcloud',
        title: 'Облако слов и модерация',
        content: (
            <>
                <p>В вопросе «Облако слов» участник пишет ответ своими словами. Главная настройка — <b>как показывать ответы</b>:</p>
                <ul>
                    <li><b>Целыми фразами</b> (по умолчанию). Ответ показывается так, как его написали. Одинаковые ответы объединяются («Много практики» и «много практики!» — один ответ) и становятся крупнее, рядом маленькой цифрой — сколько раз написали. Подходит для вопросов «Что запомнилось?», «Ваши ожидания», где отвечают словосочетаниями.</li>
                    <li><b>По словам</b>. Ответы делятся на отдельные слова. Служебные слова («и», «в», «очень», «было») не учитываются, формы одного слова объединяются («интересно», «интересная», «интересный»). Слово считается один раз на ответ. Подходит для «Опишите одним словом».</li>
                </ul>
                <p>Вид можно поменять в карточке вопроса в любой момент, даже во время опроса: облако пересчитается по уже полученным ответам. У вопросов, созданных раньше, остаётся подсчёт по словам. Самые частые ответы стоят в центре облака.</p>
                <p>Для подсчёта по словам есть дополнительные настройки:</p>
                <ul>
                    <li>минимальная и максимальная длина слова (по умолчанию 2 и 30 символов) — слишком короткие и длинные слова отбрасываются;</li>
                    <li>сколько слов учитывать из одного ответа (по умолчанию 10);</li>
                    <li>премодерация и её строгость: низкая — отклоняются только явные нарушения, средняя — обычная проверка, высокая — отклоняются и сомнительные ответы.</li>
                </ul>
                <p>С включённой премодерацией каждый ответ проверяется на оскорбления, токсичность, спам и неприемлемый контент (с помощью ИИ, если он подключён администратором, иначе — по списку запрещённых слов). Отклонённые ответы в облако не попадают. На странице результатов видно, сколько ответов одобрено, отклонено и ждёт проверки.</p>
            </>
        ),
    },
    {
        id: 'other-option',
        title: 'Вариант «Другое» со своим ответом',
        content: (
            <>
                <p>Подходит, когда заранее не перечислить все ответы: «Откуда вы узнали о мероприятии?», «Какую тему разобрать?». Участник выбирает «Другое» и пишет свой вариант.</p>
                <p><b>Как добавить.</b> В редакторе опроса нажмите «+ Добавить вопрос», выберите тип «Один вариант» или «Несколько вариантов», впишите варианты и отметьте «Добавить вариант „Другое“ со своим ответом». Название можно поменять, например на «Свой вариант». У готового вопроса «Другое» включается и выключается тумблером «Вариант „Другое“» в карточке вопроса.</p>
                <p><b>Что видит участник.</b> Вариант «Другое» стоит последним. Если его выбрать, под ним появляется поле для ответа до 100 символов. Пустое поле отправить нельзя. В вопросе с несколькими вариантами «Другое» можно отметить вместе с остальными. В Telegram-боте после выбора «Другое» бот попросит написать ответ сообщением.</p>
                <p><b>Что видно в результатах.</b> На графике «Другое» — отдельный столбец с числом выбравших. Под графиком — облако своих ответов целыми фразами: одинаковые ответы объединяются («Из рассылки» и «из рассылки!» — один ответ), частые крупнее, рядом указано, сколько раз написали.</p>
                <p><b>Проверка.</b> Каждый свой ответ проверяется автоматически, с ИИ, если его подключил администратор, иначе по списку запрещённых слов. Отклонённые ответы на экран не попадают. Обычно проверка занимает несколько секунд. В выгрузке Excel есть все ответы в виде «Другое: текст ответа», в том числе отклонённые. В отчёт попадают только одобренные.</p>
                <p>Если на «Другое» уже кто-то ответил, выключить его нельзя: иначе пропадут ответы. Если сменить тип вопроса на тип без вариантов «Другое» (например, на ранжирование), «Другое» уберётся. Если на него уже есть ответы, тип сменить не получится.</p>
            </>
        ),
    },
    {
        id: 'report',
        title: 'Отчёт по опросу',
        content: (
            <>
                <p>Кнопка «Отчёт» в карточке опроса завершает опрос и отправляет вам на email сводку:</p>
                <ul>
                    <li>главное — итог опроса в нескольких предложениях;</li>
                    <li>ключевые выводы с цифрами;</li>
                    <li>результаты по каждому вопросу: доли ответов, средние оценки, NPS, частые слова — и короткий вывод ИИ;</li>
                    <li>рекомендации по итогам.</li>
                </ul>
                <p>Отчёт приходит в течение нескольких минут на email из профиля. Повторно запросить отчёт по тому же опросу можно через пару минут. Резюме и выводы готовит ИИ — проверяйте важные цифры по разделу с результатами.</p>
                <p>Доступно на расширенном и корпоративном тарифах. Для отчёта в опросе должен быть хотя бы один ответ.</p>
            </>
        ),
    },
    {
        id: 'tariffs',
        title: 'Тарифы',
        content: (
            <>
                <div className="help-table-wrap">
                    <table className="help-table">
                        <thead>
                            <tr><th>Возможность</th><th>Бесплатный</th><th>Расширенный</th><th>Корпоративный</th></tr>
                        </thead>
                        <tbody>
                            {TARIFF_ROWS.map(([name, ...cells]) => (
                                <tr key={name}>
                                    <td>{name}</td>
                                    {cells.map((c, i) => <td key={i} className={c === '—' ? 'help-table__no' : ''}>{c}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p>Перейти на расширенный тариф и узнать его стоимость можно в профиле. Оплатить можно на 1, 3, 6 или 12 месяцев — за длинный срок действует скидка. Если у вас есть промокод, нажмите «Есть промокод?» под выбором срока: итоговая цена пересчитается сразу, а код на бесплатный доступ включит тариф без оплаты. Продление добавляется к текущему сроку. В профиле есть и личная ссылка «Пригласите коллег»: коллега получит бонусные дни к первой оплате, а вы — после его оплаты. Если оплаченный срок закончился, тариф автоматически становится бесплатным, а все опросы деактивируются — активируйте нужный. Корпоративный тариф подключается по запросу: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>
            </>
        ),
    },
    {
        id: 'account',
        title: 'Профиль и аккаунт',
        content: (
            <ul>
                <li><b>Тариф и лимиты</b> — сколько опросов создано и сколько ещё доступно.</li>
                <li><b>Пароль</b> — смена пароля; если забыли — «Забыли пароль?» на странице входа.</li>
                <li><b>Telegram</b> — если к аккаунту ранее был привязан Telegram, отвязать его можно в профиле.</li>
                <li><b>Логотип</b> — на корпоративном тарифе загрузите логотип компании, он появится на страницах опроса вместо знака СВОД.</li>
                <li><b>Оформление</b> — светлая или тёмная тема кабинета либо «Авто» (как в настройках устройства): нажмите на своё имя в правом верхнем углу. На страницы участников тема кабинета не влияет: их оформление выбирается в каждом опросе и сессии вопросов из зала.</li>
            </ul>
        ),
    },
    {
        id: 'faq',
        title: 'Частые вопросы',
        content: (
            <dl className="help-faq">
                <dt>Участники видят «Опрос не активен»</dt>
                <dd>Опрос не активирован. Нажмите «Активировать» в его карточке.</dd>
                <dt>Не получается активировать опрос</dt>
                <dd>Достигнут лимит активных опросов на вашем тарифе. Деактивируйте другой опрос или перейдите на тариф выше.</dd>
                <dt>Можно ли ответить на опрос повторно?</dt>
                <dd>Нет: один ответ с одного Telegram-аккаунта или одного браузера.</dd>
                <dt>Почему ответа нет в облаке слов?</dt>
                <dd>Ответ мог не пройти модерацию или фильтр длины слов. Количество отклонённых ответов показано на странице результатов.</dd>
                <dt>Не пришёл отчёт или письмо</dt>
                <dd>Проверьте папку «Спам» и email в профиле. Отчёт отправляется только на расширенном и корпоративном тарифах.</dd>
                <dt>Не получается зарегистрироваться</dt>
                <dd>Регистрация доступна с почтой в зоне .ru или .рф (например, name@mail.ru).</dd>
                <dt>Участник не получил презентацию</dt>
                <dd>Откройте «Ваши презентации» → «Кто запросил». «Ждёт файл» — к опросу ещё не подключён файл или файл на проверке; выберите файл в настройках опроса, и письмо уйдёт само. «Отправлено» — попросите участника проверить «Спам». Ссылка не открывается — срок хранения файла (30 дней) закончился или файл удалён.</dd>
                <dt>Почему я не вижу почту участника полностью?</dt>
                <dd>Полный адрес виден, только если участник отметил «Передать мой email организатору». Без этого согласия почту показываем скрытой — так требует закон о персональных данных.</dd>
                <dt>Файл «На проверке у модератора» — что делать?</dt>
                <dd>Ничего: модератор посмотрит файл, и если всё в порядке, письма участникам уйдут сами. Если файл заблокируют, вы увидите причину в «Ваших презентациях» и получите письмо.</dd>
                <dt>Как поделиться результатами?</dt>
                <dd>Отправьте ссылку на результаты из карточки опроса («Ссылки»), перешлите письмо с отчётом или выгрузите ответы в Excel (корпоративный тариф).</dd>
            </dl>
        ),
    },
    {
        id: 'support',
        title: 'Поддержка',
        content: (
            <p>Не нашли ответ? Напишите нам: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Опишите, что делали и что увидели, — так мы поможем быстрее.</p>
        ),
    },
];

function Help() {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [matches, setMatches] = useState(null);
    const sectionRefs = useRef({});
    const isLoggedIn = useMemo(() => {
        try {
            return Boolean(localStorage.getItem('token'));
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        document.title = 'Справка — СВОД';
        if (window.location.hash) {
            const el = document.getElementById(window.location.hash.slice(1));
            if (el) el.scrollIntoView();
        }
    }, []);

    useEffect(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            setMatches(null);
            return;
        }
        const found = SECTIONS
            .filter((s) => {
                const el = sectionRefs.current[s.id];
                return el && el.textContent.toLowerCase().includes(q);
            })
            .map((s) => s.id);
        setMatches(found);
    }, [query]);

    const visible = (id) => matches === null || matches.includes(id);

    const page = (
        <div className="help-page">
            {!isLoggedIn && (
            <header className="help-header">
                <div className="help-header__inner">
                    <div>
                        <div className="help-header__brand">СВОД</div>
                        <h1 className="help-header__title">Справка</h1>
                    </div>
                    <button type="button" className="help-header__back" onClick={() => navigate('/')}>
                        ← На главную
                    </button>
                </div>
            </header>
            )}

            <div className="help-layout">
                <aside className="help-toc" aria-label="Разделы справки">
                    <input
                        type="search"
                        className="help-search"
                        placeholder="Поиск по справке"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Поиск по справке"
                    />
                    <nav>
                        <ul>
                            {SECTIONS.filter((s) => visible(s.id)).map((s) => (
                                <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                <main className="help-content">
                    {matches !== null && matches.length === 0 && (
                        <p className="help-empty">
                            По запросу «{query}» ничего не нашлось. Попробуйте другое слово или напишите нам: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                        </p>
                    )}
                    {SECTIONS.map((s) => (
                        <section
                            key={s.id}
                            id={s.id}
                            ref={(el) => { sectionRefs.current[s.id] = el; }}
                            className="help-section"
                            hidden={!visible(s.id)}
                        >
                            <h2>{s.title}</h2>
                            {s.content}
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );

    // Организатор видит справку внутри кабинета, с тем же меню; гость — отдельной страницей
    return isLoggedIn ? <AppShell section="help" title="Справка">{page}</AppShell> : page;
}

export default Help;
